import { useContext, useEffect, useEffectEvent, useMemo } from 'react';
import { ErrorLike } from '@apollo/client';
import { skipToken, useQuery } from '@apollo/client/react';

import {
    AppPermissionsDocument,
    IAppPermissionsDomain,
    IContentTypeValue,
    PermissionsDocument,
} from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { AppPermissionsContext } from 'user/contexts/AppPermissionsContext';
import {
    TPermissionsKey,
    TPermissionTypeKey,
    TPermissionValue,
} from 'user/types';
import { parseAppPermissions } from 'user/utils/permissions';

export function useAppPermissions<
    T extends TPermissionsKey,
    K extends TPermissionTypeKey<T>,
>(objectTypeKey: T, typeKey: K): TPermissionValue {
    const appPermissions = useContext(AppPermissionsContext);

    return appPermissions?.[objectTypeKey]?.[typeKey] || {};
}

export function useAppPermissionsQuery(domain?: IAppPermissionsDomain) {
    const { showApolloError } = useApolloError();

    const { data, loading, error } = useQuery(
        AppPermissionsDocument,
        domain ? { variables: { domains: [domain] } } : skipToken
    );

    const onError = useEffectEvent((error: ErrorLike) => {
        showApolloError(error);
    });

    useEffect(() => {
        if (error) {
            onError(error);
        }
    }, [error]);

    const permissions = useMemo(
        () => (domain ? parseAppPermissions(data?.appPermissions, domain) : {}),
        [data, domain]
    );

    return { permissions, loading, error };
}

interface IUsePermissionOptions {
    category: string;
    /** Scopes the check to a single object, e.g. the user being viewed */
    targetId?: string;
    contentType?: IContentTypeValue;
}

/**
 * Checks a single permission category for a specific object.
 */
export function usePermission({
    category,
    targetId,
    contentType,
}: IUsePermissionOptions) {
    const { showApolloError } = useApolloError();

    const skip = !contentType || !targetId;

    const { data, loading, error } = useQuery(
        PermissionsDocument,
        skip ? skipToken : { variables: { category, targetId, contentType } }
    );

    const onError = useEffectEvent((error: ErrorLike) => {
        showApolloError(error);
    });

    useEffect(() => {
        if (error) {
            onError(error);
        }
    }, [error]);

    return { hasPermission: !!data?.permissions, loading, error };
}
